/** A per-page uniform grid for eraser broad-phase (plan F2).
 *
 * Every editable object registers its SEED-space bounding box; an eraser sweep
 * (a capsule: a segment inflated by a radius) queries the grid for the handful
 * of objects whose cells the capsule's bbox overlaps, and only THOSE are then
 * tested against their true geometry. That turns the old O(objects × samples)
 * rescan into O(nearby) per move.
 *
 * An R-tree is explicitly overkill at canvas-note scale (hundreds of strokes) —
 * a uniform grid keyed on bbox is simpler, allocation-light, and maintained
 * incrementally as objects enter/leave the canvas or move. Framework-free and
 * DOM-free, so it unit-tests as a plain class.
 *
 * Coordinates are the editor's immutable SEED space ([0..W0]×[0..H0], the frame
 * every Fabric object lives in forever); the eraser works in the same space, so
 * no zoom compensation is needed here. */

export interface Bbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Cell edge in seed px. ~64 keeps a US-Letter canvas page (1700×2200 seed px)
 * at a few hundred cells while keeping a stroke in only a handful. */
export const DEFAULT_CELL = 64;

export class SpatialGrid {
  private readonly cell: number;
  // "cx,cy" -> the ids whose bbox overlaps that cell.
  private readonly cells = new Map<string, Set<string>>();
  // id -> its current bbox (so an update/remove can un-home it precisely).
  private readonly boxes = new Map<string, Bbox>();

  constructor(cell: number = DEFAULT_CELL) {
    this.cell = cell > 0 ? cell : DEFAULT_CELL;
  }

  private cellKey(cx: number, cy: number): string {
    return `${cx},${cy}`;
  }

  /** The cell keys a bbox overlaps (inclusive of both extremes). floor() keeps
   * negative coordinates — a stroke that runs slightly off the page — correct. */
  private *cellsOf(b: Bbox): Iterable<string> {
    const c = this.cell;
    const x0 = Math.floor(b.minX / c);
    const y0 = Math.floor(b.minY / c);
    const x1 = Math.floor(b.maxX / c);
    const y1 = Math.floor(b.maxY / c);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) yield this.cellKey(cx, cy);
    }
  }

  /** Register (or re-home) an object's bbox. Idempotent: inserting an id already
   * present first removes its old cells, so a repeated insert never double-counts. */
  insert(id: string, bbox: Bbox): void {
    if (this.boxes.has(id)) this.remove(id);
    this.boxes.set(id, bbox);
    for (const k of this.cellsOf(bbox)) {
      let s = this.cells.get(k);
      if (!s) this.cells.set(k, (s = new Set()));
      s.add(id);
    }
  }

  /** Move an existing object to a new bbox (alias for insert, which re-homes). */
  update(id: string, bbox: Bbox): void {
    this.insert(id, bbox);
  }

  remove(id: string): void {
    const b = this.boxes.get(id);
    if (!b) return;
    for (const k of this.cellsOf(b)) {
      const s = this.cells.get(k);
      if (s) {
        s.delete(id);
        if (s.size === 0) this.cells.delete(k);
      }
    }
    this.boxes.delete(id);
  }

  has(id: string): boolean {
    return this.boxes.has(id);
  }

  clear(): void {
    this.cells.clear();
    this.boxes.clear();
  }

  get size(): number {
    return this.boxes.size;
  }

  /** Broad phase: every id whose cells the capsule (segment a→b, radius r) bbox
   * overlaps. A SUPERSET of the truly-touched objects — the caller narrows it with
   * a true-geometry test — but it never MISSES a candidate whose bbox intersects
   * the capsule bbox (they must share a cell), which is the property the eraser's
   * "never skip a stroke I swept through" correctness rests on. */
  queryCapsule(a: { x: number; y: number }, b: { x: number; y: number }, r: number): Set<string> {
    const bbox: Bbox = {
      minX: Math.min(a.x, b.x) - r,
      minY: Math.min(a.y, b.y) - r,
      maxX: Math.max(a.x, b.x) + r,
      maxY: Math.max(a.y, b.y) + r,
    };
    const out = new Set<string>();
    for (const k of this.cellsOf(bbox)) {
      const s = this.cells.get(k);
      if (s) for (const id of s) out.add(id);
    }
    return out;
  }
}
